import { useCallback, useState, useEffect, useMemo, useRef } from "react"
import { SingleSelect } from "components/styledFormElements/SingleSelect"
import { ShowItem } from "interfaces/widget-types"
import CarouselItem from "../HomePageBanner/CarouselItem"
import SearchableCategoryDropdown from '../CollectionCard/SearchableDropdown'
import { useCategoryContent } from 'hooks/category-manage'
import { useCollectionContent } from 'hooks/collection'
import SearchableCollectionDropdown from "./SearchableCollectionDropdown"
import { useShowSearch } from 'hooks/shows-master'
import { useDebounce } from 'hooks/useDebounce'
import CardDetails from './CardDetails'
import { getImageSrc, removeCloudFrontPrefix } from "services/util"
import { useNotificationBar } from 'context/notificationContext'
import { filterShowsByEpisodes, getShowIds, checkShowHasMinEpisodes, MAX_ITEMS } from './showHelpers'
import { getSelectedShowIds, getShowItems, getCardDetails, initializeStateFromShowItems, SourceTypeState } from './sourceTypeHelpers'
import { SelectableShowCards } from './SelectableShowCards'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { X } from 'lucide-react'
import { useGetIcons } from 'hooks/useIconMaster'
import { useGetWidgetBusinessRule } from "hooks/business-rule"
import { useGetItemsParent } from 'hooks/category-manage'
import {
    buildShowCardSourceParentFields,
    collectShowCardParentRequests,
    createParentCache,
    enrichShowCardWidgetItems,
    EMPTY_PARENT_LINK_PAYLOAD,
    resolveParentsForRequests,
    shouldResolveShowCardParents,
} from './linkedParentResolver'

interface Widget {
    value: string | number
    label: string
    code?: string
    image?: string
}

interface Props {
    widget: Widget
    items?: ShowItem[]
    onSave: (payload: any) => void
    initialData?: any
    onCancel?: () => void;
    template_type?: string;
}

interface FormErrors {
    widgetTitle?: string;
    widgetIcon?: string;
    sortingMethod?: string;
    showItems?: string;
};
const MAX_TITLE_LENGTH = 25;
const ShowCard = ({ widget, items = [], onSave, initialData, template_type }: Props) => {
    const notification = useNotificationBar()
    const itemsParentMutation = useGetItemsParent()
    const [errors, setErrors] = useState<FormErrors>({});
    const [widgetName, setWidgetName] = useState("Show Card")
    const [showItems, setShowItems] = useState<ShowItem[]>(items)
    const [contentType, setContentType] = useState('Static')
    const [sourceType, setSourceType] = useState('Manual')
    // const [sortingMethod, setSortingMethod] = useState('Show Priority')
    const [sortingMethod, setSortingMethod] = useState('SHOW PRIORITY')
    const [enableChevron, setEnableChevron] = useState(false)
    const [customRedirectUrl, setCustomRedirectUrl] = useState("")
    const [contentTypeArr, setContentTypeArr] = useState<any>(['Static', 'Contextual']);
    const [contextualError, setContextualError] = useState(false)
    const [widgetTitle, setWidgetTitle] = useState('')
    const [contextualRule, setContextualRule] = useState<number | ''>('')
    const [widgetIcon, setWidgetIcon] = useState<{
        name: string;
        icon: string;
    } | null>(null);

    const [selectedCategory, setSelectedCategory] = useState<any>(null)
    const [selectedCollection, setSelectedCollection] = useState<any>(null)

    const [showSearch, setShowSearch] = useState('')
    const [showSearchResults, setShowSearchResults] = useState<any[]>([])
    const [showSearchOpen, setShowSearchOpen] = useState(false)
    const showSearchRef = useRef<HTMLDivElement>(null)
    const debouncedShowSearch = useDebounce(showSearch, 400)

    const showSearchQuery = useShowSearch(debouncedShowSearch)

    const { data: iconsResponse } = useGetIcons();

    const icons =
        Array.isArray(iconsResponse?.data?.results)
            ? iconsResponse.data.results
            : [];


    const [filteredShows, setFilteredShows] = useState<any[]>([])
    const [isFilteringShows, setIsFilteringShows] = useState(false)

    const [categoryState, setCategoryState] = useState<SourceTypeState>({
        selectedShowIds: [],
        showItems: [],
        cardDetails: {}
    })
    const [collectionState, setCollectionState] = useState<SourceTypeState>({
        selectedShowIds: [],
        showItems: [],
        cardDetails: {}
    })

    const [cardDetails, setCardDetails] = useState<Record<string, any>>({})

    const isInitialized = useRef(false)
    const lastInitialDataRef = useRef<any>(null)
    const manualDataBackup = useRef<{
        showItems: ShowItem[]
        cardDetails: Record<string, any>
    } | null>(null)
    // Separate backups for Category and Collection to persist data when switching between them
    const categoryDataBackup = useRef<{
        showItems: ShowItem[]
        cardDetails: Record<string, any>
        selectedCategory?: any
    } | null>(null)
    const collectionDataBackup = useRef<{
        showItems: ShowItem[]
        cardDetails: Record<string, any>
        selectedCollection?: any
    } | null>(null)

    const sortingOptions = [
        { value: 'SHOW PRIORITY', label: 'Show Priority' },
        { value: 'HIGHTEST ENGAGEMENT', label: 'Highest Engagement' },
        { value: 'LATEST', label: 'Latest' }
    ]

    // Store category/collection IDs from initialData for fetching
    const [initialCategoryId, setInitialCategoryId] = useState<string | null>(null)
    const [initialCollectionId, setInitialCollectionId] = useState<string | null>(null)

    // Fetch category/collection data for initialization
    const initialCategoryQuery = useCategoryContent(initialCategoryId, {
        enabled: !!initialCategoryId,
    })

    const initialCollectionQuery = useCollectionContent(initialCollectionId, {
        enabled: !!initialCollectionId,
    })

    const categoryContentQuery = useCategoryContent(selectedCategory?.id || null, {
        enabled: !!selectedCategory?.id && sourceType === 'Category',
    })

    const collectionContentQuery = useCollectionContent(selectedCollection?.id || null, {
        enabled: !!selectedCollection?.id && sourceType === 'Collection',
    })

    const { data: businessRuleResponse, } = useGetWidgetBusinessRule({}, { type: widget?.code ? widget.code : 'video_carousel' })

    const businessWidgetRule = useMemo(() => businessRuleResponse?.data, [businessRuleResponse])
    const contextualOptions = useMemo(
        () =>
            (businessWidgetRule || []).map((r: any) => ({
                name: r.rule_name,
                value: r.slug,
                id: r.id,
            })),
        [businessWidgetRule]
    );

    const rawShows = useMemo(() => {
        if (sourceType === 'Category' && categoryContentQuery.data?.data) {
            const categoryData = categoryContentQuery.data.data
            const shows = categoryData.category?.shows || []
            return shows
        }
        if (sourceType === 'Collection' && collectionContentQuery.data?.data) {
            const collectionData = collectionContentQuery.data.data
            const shows = collectionData.collection?.shows || []
            return shows
        }
        return []
    }, [sourceType, categoryContentQuery.data, collectionContentQuery.data])

    useEffect(() => {
        if (sourceType !== 'Category' && sourceType !== 'Collection') {
            setFilteredShows([])
            setIsFilteringShows(false)
            return
        }

        if (rawShows.length === 0) {
            setFilteredShows([])
            setIsFilteringShows(false)
            return
        }

        const filterShows = async () => {
            setIsFilteringShows(true)
            try {
                const filtered = await filterShowsByEpisodes(rawShows)
                setFilteredShows(filtered)
            } catch (error) {
                console.error('Error filtering shows:', error)
                setFilteredShows([])
            } finally {
                setIsFilteringShows(false)
            }
        }

        filterShows()
    }, [rawShows, sourceType])

    const availableShows = useMemo(() => filteredShows, [filteredShows])

    const manualShowIds = useMemo(() => getShowIds(showItems), [showItems])

    // Get current state based on source type
    const currentSelectedShowIds = useMemo(() =>
        getSelectedShowIds(sourceType as 'Category' | 'Collection' | 'Manual', categoryState, collectionState, showItems),
        [sourceType, categoryState, collectionState, showItems]
    )

    const currentShowItems = useMemo(() =>
        getShowItems(sourceType as 'Category' | 'Collection' | 'Manual', categoryState, collectionState, showItems),
        [sourceType, categoryState, collectionState, showItems]
    )

    const currentCardDetails = useMemo(() =>
        getCardDetails(sourceType as 'Category' | 'Collection' | 'Manual', categoryState, collectionState, cardDetails),
        [sourceType, categoryState, collectionState, cardDetails]
    )
    // Restore state when shows are loaded (for initialData)
    useEffect(() => {
        if (sourceType === 'Category' && selectedCategory && filteredShows.length > 0) {
            const backup = categoryDataBackup.current
            if (backup && backup.showItems.length > 0 && categoryState.showItems.length === 0) {
                const backupShowIds = new Set(backup.showItems.map(item => String(item.id || item.redirection_id)))
                const restoredShows = filteredShows
                    .filter((show: any) => {
                        const showId = (show.show?.id || show.id)?.toString()
                        return showId && backupShowIds.has(showId)
                    })
                    .map((show: any) => ({
                        id: (show.show?.id || show.id)?.toString() || '',
                        thumbnail: '',
                        redirection_to: 'SHOW' as any,
                        redirection_id: (show.show?.id || show.id)?.toString() || '',
                    }))

                if (restoredShows.length > 0) {
                    setCategoryState(initializeStateFromShowItems(restoredShows.slice(0, MAX_ITEMS), backup.cardDetails))
                }
            }
        } else if (sourceType === 'Collection' && selectedCollection && filteredShows.length > 0) {
            const backup = collectionDataBackup.current
            if (backup && backup.showItems.length > 0 && collectionState.showItems.length === 0) {
                const backupShowIds = new Set(backup.showItems.map(item => String(item.id || item.redirection_id)))
                const restoredShows = filteredShows
                    .filter((show: any) => {
                        const showId = (show.show?.id || show.id)?.toString()
                        return showId && backupShowIds.has(showId)
                    })
                    .map((show: any) => ({
                        id: (show.show?.id || show.id)?.toString() || '',
                        thumbnail: '',
                        redirection_to: 'SHOW' as any,
                        redirection_id: (show.show?.id || show.id)?.toString() || '',
                    }))

                if (restoredShows.length > 0) {
                    setCollectionState(initializeStateFromShowItems(restoredShows.slice(0, MAX_ITEMS), backup.cardDetails))
                }
            }
        }
    }, [sourceType, selectedCategory, selectedCollection, filteredShows, categoryState.showItems.length, collectionState.showItems.length])

    useEffect(() => {
        if (sourceType === 'Manual' && showItems.length > MAX_ITEMS) {
            setShowItems(prev => prev.slice(0, MAX_ITEMS))
        }
    }, [showItems.length, sourceType])

    useEffect(() => {
        if (sourceType !== 'Manual') {
            return
        }
        const validShowIds = new Set<string>()
        showItems.forEach(item => {
            if (item.id) validShowIds.add(String(item.id))
            if (item.redirection_id) validShowIds.add(String(item.redirection_id))
        })

        setCardDetails(prev => {
            if (showItems.length === 0) {
                return {}
            }

            const filtered: Record<string, any> = {}
            Object.keys(prev).forEach(showId => {
                if (validShowIds.has(showId)) {
                    filtered[showId] = prev[showId]
                }
            })

            const prevKeys = Object.keys(prev)
            const filteredKeys = Object.keys(filtered)
            if (prevKeys.length !== filteredKeys.length) {
                return filtered
            }

            // Return previous value if nothing changed (to avoid unnecessary re-renders)
            return prev
        })
    }, [showItems, sourceType])

    useEffect(() => {
        if (!debouncedShowSearch.trim() || !showSearchQuery.data) {
            setShowSearchResults([])
            return
        }

        const filterShows = async () => {
            try {
                const results = showSearchQuery.data?.results || showSearchQuery.data || []
                const allShows = (results || []).map((show: any) => ({
                    id: show.id?.toString() || '',
                    label: show.name || '',
                    thumbnail: show.thumbnail || '',
                }))

                const checkPromises = allShows.map(async (show) => {
                    const hasMinEpisodes = await checkShowHasMinEpisodes(show.id, 2)
                    return hasMinEpisodes ? show : null
                })

                const checkedResults = await Promise.all(checkPromises)
                const filtered = checkedResults.filter((show): show is any => show !== null)
                setShowSearchResults(filtered)
            } catch (error) {
                console.error('Error filtering shows:', error)
                setShowSearchResults([])
            }
        }

        filterShows()
    }, [showSearchQuery.data, debouncedShowSearch])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!showSearchRef.current?.contains(e.target as Node)) {
                setShowSearchOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        if (lastInitialDataRef.current !== initialData) {
            isInitialized.current = false
            lastInitialDataRef.current = initialData
        }

        if (!initialData) {
            if (isInitialized.current) {
                setWidgetName("")
                setShowItems([])
                setContentType('Static')
                setSourceType('Manual')
                setSortingMethod('SHOW PRIORITY')
                setEnableChevron(false)
                setCustomRedirectUrl("")
                setWidgetTitle('')
                setWidgetIcon(null)
                //setWidgetIconPreview(null)
                setSelectedCategory(null)
                setSelectedCollection(null)
                setCardDetails({})
                setCategoryState({ selectedShowIds: [], showItems: [], cardDetails: {} })
                setCollectionState({ selectedShowIds: [], showItems: [], cardDetails: {} })
                isInitialized.current = false
            }
            return
        }
        if (isInitialized.current) return

        const widgetData = initialData.data || {}

        const nameValue = initialData.name || widgetData.widget_name || widget.label || ''
        setWidgetName(nameValue)

        setWidgetTitle(widgetData.widget_title || '')
        const contentTypeValue = widgetData.content_type
            ? (widgetData.content_type.toUpperCase() === 'STATIC'
                ? 'Static'
                : widgetData.content_type.toUpperCase() === 'CONTEXTUAL'
                    ? 'Contextual'
                    : widgetData.content_type)
            : 'Static'
        setContentType(contentTypeValue)
        setContextualRule(initialData.data.content_rule_id || '') // Fill contextualRule

        const sourceTypeValue = widgetData.source_type
            ? (widgetData.source_type.toUpperCase() === 'MANUAL'
                ? 'Manual'
                : widgetData.source_type.toUpperCase() === 'CATEGORY'
                    ? 'Category'
                    : widgetData.source_type.toUpperCase() === 'COLLECTION'
                        ? 'Collection'
                        : widgetData.source_type)
            : 'Manual'
        setSourceType(sourceTypeValue)

        const sortingValue = widgetData.sorting_method || 'SHOW PRIORITY'
        setSortingMethod(sortingValue)

        // const extractBaseIconKey = (path: string) => {
        //     if (!path) return '';

        //     const fileName = path.split('/').pop()?.toLowerCase() || '';
        //     const withoutExt = fileName.replace('.svg', '');

        //     // Remove timestamp + hash
        //     return withoutExt.split('_')[0];
        // };

        if (widgetData.widget_icon) {
            setWidgetIcon({
                name: widgetData.widget_icon_name || '',
                icon: widgetData.widget_icon,
            });
        } else {
            setWidgetIcon(null);
        }

        if (widgetData.chevron_redirection) {
            setEnableChevron(true)
            setCustomRedirectUrl(widgetData.chevron_redirection)
        } else {
            setEnableChevron(false)
            setCustomRedirectUrl("")
        }

        // Set category/collection IDs for fetching
        if (sourceTypeValue === 'Category' && widgetData.category_id) {
            const categoryId = String(widgetData.category_id)
            setInitialCategoryId(categoryId)
            if (!selectedCategory || String(selectedCategory.id) !== categoryId) {
                setSelectedCategory({
                    id: categoryId,
                    name: widgetData.category_name || '',
                })
            }
        } else {
            setInitialCategoryId(null)
            if (sourceTypeValue !== 'Category') {
                setSelectedCategory(null)
            }
        }

        if (sourceTypeValue === 'Collection' && widgetData.collection_id) {
            const collectionId = String(widgetData.collection_id)
            setInitialCollectionId(collectionId)
            if (!selectedCollection || String(selectedCollection.id) !== collectionId) {
                setSelectedCollection({
                    id: collectionId,
                    name: widgetData.collection_name || '',
                })
            }
        } else {
            setInitialCollectionId(null)
            if (sourceTypeValue !== 'Collection') {
                setSelectedCollection(null)
            }
        }
        if (widgetData.widget_items && Array.isArray(widgetData.widget_items)) {
            if (sourceTypeValue === 'Manual') {
                const cleanedItems = widgetData.widget_items.slice(0, MAX_ITEMS).map((item: any) => ({
                    ...item,
                    thumbnail: item.thumbnail ? removeCloudFrontPrefix(item.thumbnail) : ''
                }))
                setShowItems(cleanedItems)
            }
        } else {
            if (sourceTypeValue === 'Manual') {
                setShowItems([])
            }
        }

        if (widgetData.widget_items && Array.isArray(widgetData.widget_items)) {
            const itemsForCardDetails = widgetData.widget_items.slice(0, MAX_ITEMS)
            const details: Record<string, any> = {}
            itemsForCardDetails.forEach((item: any) => {
                if (item.card_details && item.id) {
                    const topEpisodeId = item.card_details.top_thumbnail_episode_id
                    const bottomEpisodeId = item.card_details.bottom_thumbnail_episode_id
                    const selectedEpisodes = [
                        topEpisodeId ? String(topEpisodeId) : null,
                        bottomEpisodeId ? String(bottomEpisodeId) : null
                    ].filter(Boolean) as string[]

                    details[String(item.id)] = {
                        title: item.card_details.title || '',
                        shortSummary: item.card_details.short_summary || '',
                        episodeCount: item.card_details.episode_count || 0,
                        selectedEpisodes: selectedEpisodes
                    }
                }
            })

            // Initialize separate state for Category and Collection
            const limitedItems = (widgetData.widget_items || []).slice(0, MAX_ITEMS)
            if (sourceTypeValue === 'Manual') {
                // Set Manual state
                setCardDetails(details)
                manualDataBackup.current = {
                    showItems: limitedItems,
                    cardDetails: details
                }
            } else if (sourceTypeValue === 'Category') {
                // Initialize Category state, clear Manual state
                setCardDetails({})
                const categoryStateData = initializeStateFromShowItems(limitedItems, details)
                setCategoryState(categoryStateData)
                categoryDataBackup.current = {
                    showItems: limitedItems,
                    cardDetails: details,
                    selectedCategory: { id: widgetData.category_id }
                }
            } else if (sourceTypeValue === 'Collection') {
                // Initialize Collection state, clear Manual state
                setCardDetails({})
                const collectionStateData = initializeStateFromShowItems(limitedItems, details)
                setCollectionState(collectionStateData)
                collectionDataBackup.current = {
                    showItems: limitedItems,
                    cardDetails: details,
                    selectedCollection: { id: widgetData.collection_id }
                }
            }
        } else {
            // No widget items - clear all states based on source type
            if (sourceTypeValue === 'Manual') {
                setCardDetails({})
            } else {
                setCardDetails({})
            }
        }

        isInitialized.current = true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, widget.label, icons])

    // Set category name when initial category data is fetched
    useEffect(() => {
        if (initialCategoryQuery.data && initialCategoryId) {
            // Try different response structures: data.data.data, data.data.category, or data.data
            const responseData = initialCategoryQuery.data.data || initialCategoryQuery.data
            const categoryData = responseData?.category || responseData?.data || responseData
            if (categoryData?.id && categoryData?.name && String(categoryData.id) === String(initialCategoryId)) {
                // Always update if the category data matches the initialCategoryId
                setSelectedCategory({
                    id: categoryData.id,
                    name: categoryData.name
                })
                setInitialCollectionId(null) // Clear after setting
            }
        }
    }, [initialCategoryQuery.data, initialCategoryId])

    // Set collection name when initial collection data is fetched
    useEffect(() => {
        if (initialCollectionQuery.data && initialCollectionId) {
            // Try different response structures: data.data.collection or data.data
            const responseData = initialCollectionQuery.data.data || initialCollectionQuery.data
            const collectionData = responseData?.collection || responseData
            if (collectionData?.id && collectionData?.name && String(collectionData.id) === String(initialCollectionId)) {
                // Always update if the collection data matches the initialCollectionId
                setSelectedCollection({
                    id: collectionData.id,
                    name: collectionData.name
                })
                setInitialCollectionId(null) // Clear after setting
            }
        }
    }, [initialCollectionQuery.data, initialCollectionId])

    // Update widgetTitle and widgetIcon when Category is selected
    useEffect(() => {
        // Only update if Category is selected and data is loaded (skip during initial load from initialData)
        if (sourceType === 'Category' && categoryContentQuery.data && selectedCategory?.id) {
            // Skip if we're still initializing from initialData
            if (!isInitialized.current) return

            const responseData = categoryContentQuery.data.data || categoryContentQuery.data
            const categoryData = responseData?.category || responseData
            if (categoryData?.name) {
                setWidgetTitle(categoryData.name)
            }
            if (categoryData?.icon) {
                const iconUrl = categoryData.icon.startsWith('http')
                    ? categoryData.icon
                    : getImageSrc(categoryData.icon)
                //setWidgetIconPreview(iconUrl)
                setWidgetIcon(null)
            }
        }
    }, [sourceType, categoryContentQuery.data, selectedCategory?.id])

    // Update widgetTitle and widgetIcon when Collection is selected
    useEffect(() => {
        // Only update if Collection is selected and data is loaded (skip during initial load from initialData)
        if (sourceType === 'Collection' && collectionContentQuery.data && selectedCollection?.id) {
            // Skip if we're still initializing from initialData
            if (!isInitialized.current) return

            const responseData = collectionContentQuery.data.data || collectionContentQuery.data
            const collectionData = responseData?.collection || responseData
            if (collectionData?.name) {
                setWidgetTitle(collectionData.name)
            }
            if (collectionData?.icon) {
                const iconUrl = collectionData.icon.startsWith('http')
                    ? collectionData.icon
                    : getImageSrc(collectionData.icon)
                //setWidgetIconPreview(iconUrl)
                setWidgetIcon(null) // Clear file if icon comes from collection
            }
        }
    }, [sourceType, collectionContentQuery.data, selectedCollection?.id])

    // Clear widgetTitle and widgetIcon when switching to Manual (unless initialData exists)
    useEffect(() => {
        // Only clear if Manual is selected and we're not initializing from initialData
        if (sourceType === 'Manual' && isInitialized.current && !initialData) {
            setWidgetTitle('')
            //setWidgetIcon(null)
            //setWidgetIconPreview(null)
        }
    }, [sourceType, initialData])

    const moveItem = (from: number, to: number) => {
        setShowItems(prev => {
            if (to < 0 || to >= prev.length) return prev

            const updated = [...prev]
                ;[updated[from], updated[to]] = [updated[to], updated[from]]
            return updated
        })
    }

    const deleteItem = (index: number) => {
        // setShowItems(prev => prev.filter((_, i) => i !== index))
        setShowItems(prev => {
            const updated = prev.filter((_, i) => i !== index)
            if (updated.length < MAX_ITEMS) {
                clearFieldError('showItems')
            }
            return updated
        })
    }

    const handleShowSelect = useCallback((show: any) => {
        setShowItems(prev => {
            // if (prev.length >= MAX_ITEMS) return prev
            if (prev.length >= MAX_ITEMS) {
                setFieldError(
                    'showItems',
                    `You can choose only ${MAX_ITEMS} items`
                )
                return prev
            }
            const alreadyAdded = prev.some(item => item.id === show.id)
            if (alreadyAdded) return prev

            // Remove CloudFront URL prefix from thumbnail before storing
            const cleanThumbnail = removeCloudFrontPrefix(show?.thumbnail || '')

            const newItem: ShowItem = {
                id: show?.id,
                thumbnail: cleanThumbnail,
                title: show?.label,
                redirection_to: 'SHOW' as any,
                redirection_id: show?.id,
            }
            const updated = [...prev, newItem]
            return updated.slice(0, MAX_ITEMS)
        })
        // ✅ Clear error ONLY when exactly valid
        if (showItems.length === MAX_ITEMS) {
            clearFieldError('showItems');
        }
        setShowSearch('')
        setShowSearchOpen(false)
    }, [])

    const handleWidgetTitleChange = (value: string) => {
        setWidgetTitle(value);
        if (!value.trim()) {
            setFieldError('widgetTitle', 'Widget Title is required');
            return;
        }
        if (value.length > MAX_TITLE_LENGTH) {
            setFieldError(
                'widgetTitle',
                `Widget Title cannot exceed ${MAX_TITLE_LENGTH} characters`
            );
            return;
        }
        clearFieldError('widgetTitle');
    };

    const saveShowDataWithCardDetails = useCallback(() => {
        const currentItems = currentShowItems
        const currentDetails = currentCardDetails
        const selectedIds = currentSelectedShowIds

        if (sourceType === 'Manual') {
            return currentItems.map(item => {
                const showId = String(item.id || item.redirection_id || '')
                const cardDetail = currentDetails[showId] || currentDetails[item.id] || currentDetails[item.redirection_id]
                if (cardDetail) {
                    return {
                        ...item,
                        card_details: {
                            title: cardDetail.title || '',
                            short_summary: cardDetail.shortSummary || '',
                            episode_count: cardDetail.episodeCount || 0,
                            top_thumbnail_episode_id: cardDetail.selectedEpisodes?.[0] || null,
                            bottom_thumbnail_episode_id: cardDetail.selectedEpisodes?.[1] || null,
                        }
                    }
                }
                return item
            })
        }

        if (sourceType === 'Category' || sourceType === 'Collection') {
            return selectedIds.map((showId: string) => {
                const showIdStr = String(showId).trim()
                let cardDetail = currentDetails[showIdStr]
                    || currentDetails[showId]
                    || currentDetails[String(showId)]

                if (!cardDetail) {
                    const matchingKey = Object.keys(currentDetails).find(key =>
                        String(key).trim() === showIdStr || String(key) === String(showId)
                    )
                    if (matchingKey) {
                        cardDetail = currentDetails[matchingKey]
                    }
                }

                const baseItem = {
                    id: showIdStr,
                    thumbnail: '',
                    redirection_to: 'SHOW' as any,
                    redirection_id: showIdStr,
                }

                if (cardDetail && (cardDetail.title || cardDetail.selectedEpisodes?.length > 0)) {
                    return {
                        ...baseItem,
                        card_details: {
                            title: cardDetail.title || '',
                            short_summary: cardDetail.shortSummary || '',
                            episode_count: cardDetail.episodeCount || 0,
                            top_thumbnail_episode_id: cardDetail.selectedEpisodes?.[0] || null,
                            bottom_thumbnail_episode_id: cardDetail.selectedEpisodes?.[1] || null,
                        }
                    }
                }
                return baseItem
            }).slice(0, MAX_ITEMS)
        }

        return []
    }, [sourceType, currentShowItems, currentCardDetails, currentSelectedShowIds])

    useEffect(() => {
        if (template_type === 'HOME' || template_type === 'EXPLORE' || template_type === 'SHOW') {
            setContentTypeArr(['Static']);
        }
    }, [template_type])

    const handleSave = async () => {
        const isManual = sourceType === 'Manual'
        if (contentType === 'Static' && isManual && (!widgetTitle || !widgetIcon)) {
            setFieldError('widgetIcon', 'Widget Icon is required')
            setFieldError('widgetTitle', 'Widget Title is required')
            return
        }
        if (contentType === 'Static' && isManual && currentShowItems.length === 0) {
            setFieldError('showItems', 'Please select at least one show');
            return;
        }
        const hasEmptySelectedEpisodes = Object.values(currentCardDetails || {}).some(
            (card: any) => !card.selectedEpisodes || card.selectedEpisodes.length === 0
        )
        if (contentType === 'Static' && hasEmptySelectedEpisodes) {
            notification.push({
                message: 'Please select at least one episode for each show inside configuration',
                type: 'error',
            })
            return
        }
        /* ---------- VALIDATION ---------- */
        if (contentType === 'Static' && sourceType === 'Category') {
            if (!selectedCategory?.id || !sortingMethod || currentSelectedShowIds.length === 0) {
                notification.push({
                    title: "Error",
                    message: 'Please fill all required fields and select at least one show',
                    type: 'error',
                })
                return
            }
        } else if (contentType === 'Static' && sourceType === 'Collection') {
            if (!selectedCollection?.id || !sortingMethod || currentSelectedShowIds.length === 0) {
                notification.push({
                    message: 'Please fill all required fields and select at least one show',
                    type: 'error',
                })
                return
            }
        } else if (contentType === 'Static' && sourceType === 'Collection' || sourceType === 'Category') {
            if (!sortingMethod) {
                notification.push({
                    message: 'Please select sorting method',
                    type: 'error',
                })
                return
            }
        }
        else if (contentType === 'Static' && isManual && showItems.length === 0) {
            notification.push({
                title: 'Error',
                message: 'Please add at least one item',
                type: 'error',
            })
            return
        }

        if (contentType === 'Contextual') {
            if (!contextualRule || contextualRule === 0) {
                setContextualError(true)
                return
            }
        }

        // let widgetIconKey = widgetIcon?.icon || ''
        const resolvedWidgetTitle = isManual
            ? widgetTitle
            : sourceType === 'Category'
                ? selectedCategory?.name || ''
                : selectedCollection?.name || ''

        let resolvedWidgetIcon = '';

        if (widgetIcon?.icon) {
            resolvedWidgetIcon = widgetIcon.icon.startsWith('http')
                ? new URL(widgetIcon.icon).pathname.replace(/^\/+/, '')
                : widgetIcon.icon;
        }

        let enrichedShowItems = saveShowDataWithCardDetails()
        let widgetLevelParents = { ...EMPTY_PARENT_LINK_PAYLOAD }

        if (shouldResolveShowCardParents(contentType, sourceType)) {
            try {
                const parentCache = createParentCache()
                const collectionSourceId =
                    sourceType === 'Collection' ? selectedCollection?.id : undefined

                const parentRequests = collectShowCardParentRequests(
                    sourceType,
                    enrichedShowItems,
                    { sourceId: collectionSourceId }
                )

                await resolveParentsForRequests(
                    itemsParentMutation.mutateAsync,
                    parentCache,
                    parentRequests
                )

                widgetLevelParents = buildShowCardSourceParentFields(
                    sourceType,
                    parentCache,
                    {
                        sourceId: collectionSourceId,
                        sourceName: selectedCollection?.name,
                        categoryId: selectedCategory?.id,
                        categoryName: selectedCategory?.name,
                    }
                )

                enrichedShowItems = enrichShowCardWidgetItems(
                    enrichedShowItems,
                    parentCache
                )
            } catch {
                notification.push({
                    title: 'Error',
                    message: 'Failed to resolve parent data. Please try again.',
                    type: 'error',
                })
                return
            }
        }

        const payload: any = {
            name: widgetName,
            description: '',
            widget_type: widget.value || 0,
            widget_code: widget.code,
            data: {
                widget_name: widgetName,
                widget_code: widget.code,
                widget_title: resolvedWidgetTitle,
                widget_icon: resolvedWidgetIcon,
                widget_icon_name: widgetIcon?.name || '',
                content_type: contentType?.toUpperCase() || "",
                source_type: sourceType?.toUpperCase() || "",
                ...widgetLevelParents,
                widget_items: enrichedShowItems,
            },
            status: 1,
        }

        if (contentType === 'Static' && (sourceType === 'Category' || sourceType === 'Collection')) {
            payload.data.sorting_method = sortingMethod
            payload.data.view_all = true
            if (sourceType === 'Category') {
                payload.data.source_id = selectedCategory?.id || ''
                payload.data.category_id = selectedCategory?.id || ''
            }
            if (sourceType === 'Collection') {
                payload.data.source_id = selectedCollection?.id || ''
                payload.data.collection_id = selectedCollection?.id || ''
            }
        }

        if (contentType === 'Static' && isManual && enableChevron) {
            payload.data.chevron_redirection = customRedirectUrl
        }

        if (contentType === 'Contextual') {
            payload.data.widget_items = []
            payload.data.widget_title = ''
            payload.data.widget_icon = ''
            payload.data.widget_icon_name = ''
            payload.data.source_id = ''
            payload.data.source_name = ''
            payload.data.view_all = false
        }
        if (contentType === 'Contextual') {
            const selectedRule = contextualOptions.find(
                (rule) => rule.id === Number(contextualRule)
            )

            payload.data.content_rule_id = contextualRule
            payload.data.content_rule = selectedRule?.value || ''
        }

        onSave(payload)

        // ✅ SUCCESS NOTIFICATION
        notification.push({
            title: 'Success',
            message: 'Show Grid widget saved successfully',
            type: 'success',
        })
    }

    const handleSourceTypeChange = (newSourceType: string) => {
        const previousSourceType = sourceType

        if (previousSourceType === 'Manual') {
            manualDataBackup.current = {
                showItems: [...showItems],
                cardDetails: { ...cardDetails }
            }
        } else if (previousSourceType === 'Category') {
            categoryDataBackup.current = {
                showItems: categoryState.showItems,
                cardDetails: categoryState.cardDetails,
                selectedCategory: selectedCategory
            }
        } else if (previousSourceType === 'Collection') {
            collectionDataBackup.current = {
                showItems: collectionState.showItems,
                cardDetails: collectionState.cardDetails,
                selectedCollection: selectedCollection
            }
        }

        // Restore state for the new source type
        if (newSourceType === 'Manual') {
            if (manualDataBackup.current) {
                // Restore from backup
                setShowItems(manualDataBackup.current.showItems.slice(0, MAX_ITEMS))
                setCardDetails(manualDataBackup.current.cardDetails)
            } else {
                // Clear Manual state if no backup exists (fresh Manual mode)
                setShowItems([])
                setCardDetails({})
            }
        } else if (newSourceType === 'Category') {
            if (categoryDataBackup.current?.selectedCategory) {
                setSelectedCategory(categoryDataBackup.current.selectedCategory)
            }
            // Clear Manual state when switching away from it
            if (previousSourceType === 'Manual') {
                setShowItems([])
                setCardDetails({})
            }
        } else if (newSourceType === 'Collection') {
            if (collectionDataBackup.current?.selectedCollection) {
                setSelectedCollection(collectionDataBackup.current.selectedCollection)
            }
            // Clear Manual state when switching away from it
            if (previousSourceType === 'Manual') {
                setShowItems([])
                setCardDetails({})
            }
        }

        setSourceType(newSourceType)
        if (newSourceType !== 'Category') {
            setSelectedCategory(null)
        }
        if (newSourceType !== 'Collection') {
            setSelectedCollection(null)
        }
        if (newSourceType === 'Manual') {
            if (initialData?.data?.widget_title) {
                setWidgetTitle(initialData.data.widget_title)
            } else {
                setWidgetTitle('')
            }

            if (initialData?.data?.widget_icon && icons.length > 0) {
                const savedPath = initialData.data.widget_icon
                const cleanFileName = savedPath.split('/').pop() || ''

                const matchedIcon = icons.find((icon: any) => {
                    const iconPath = icon.icon || icon.path || icon.image || ''
                    return iconPath.includes(cleanFileName)
                })

                if (matchedIcon) {
                    setWidgetIcon(matchedIcon)
                } else {
                    setWidgetIcon(null)
                }
            } else {
                setWidgetIcon(null)
            }
        }
    }

    const setFieldError = useCallback((field: string, message: string) => {
        setErrors(prev => ({
            ...prev,
            [field]: message,
        }));
    }, []);

    const clearFieldError = (key: keyof FormErrors) => {
        setErrors((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    };

    const handleCancel = () => {
        onSave(initialData || null);
    };
    return (
        <div className="space-y-6 mb-1">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 space-y-3">
                    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                        <div className="flex flex-col gap-3 mb-4">
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300 block">
                                Content Type
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                        Static (same shows everywhere), Contextual (page-specific shows). Only shows with ≥2 episodes qualify.
                                    </div>
                                </span>
                            </div>
                            <div className="flex items-center gap-4 flex-wrap">
                                {contentTypeArr.map(type => (
                                    <label key={type} className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            value={type}
                                            checked={contentType === type}
                                            onChange={() => setContentType(type)}
                                            className="w-4 h-4"
                                        />
                                        <span className="capitalize text-sm dark:text-white">{type}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        {/* Added for contentType contextual */}
                        {contentType === 'Contextual' && (
                            <>
                                {/* Widget Rule Select */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        Select Widget Rule
                                        <span className="relative group ml-1 inline-block">
                                            <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer inline" />

                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                                show or episodes for current collection page or category page.
                                            </div>
                                        </span>
                                    </label>

                                    <select
                                        value={contextualRule}
                                        onChange={(e) => setContextualRule(
                                            e.target.value ? Number(e.target.value) : ''
                                        )}
                                        className={` 
            peer block w-full rounded-lg bg-transparent  text-sm
            focus:outline-none focus:ring-1  focus:ring-blue-500
            disabled:opacity-70 disabled:pointer-events-none
            px-3 py-2 border border-gray-300 dark:border-slate-600
          `}
                                    >
                                        <option value="">Select Option</option>
                                        {contextualOptions.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                    {(!contextualRule || contextualRule === 0) && contextualError && (
                                        <p className="text-red-500 text-xs">
                                            Please select a widget rule
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                        {contentType === 'Static' && (
                            <div className="flex flex-col gap-3 mb-4">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Source Type
                                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                            Category (auto-pulls shows), Collection (auto-pulls shows), Manual (hand-pick shows).
                                        </div>
                                    </span>
                                </span>
                                <div className="flex items-center gap-4 flex-wrap">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="sourceType"
                                            checked={sourceType === 'Category'}
                                            onChange={() => handleSourceTypeChange('Category')}
                                            className="h-4 w-4 accent-blue-600"
                                        />
                                        <span className="text-sm dark:text-white">Category</span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="sourceType"
                                            checked={sourceType === 'Collection'}
                                            onChange={() => handleSourceTypeChange('Collection')}
                                            className="h-4 w-4 accent-blue-600"
                                        />
                                        <span className="text-sm dark:text-white">Collection</span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="sourceType"
                                            checked={sourceType === 'Manual'}
                                            onChange={() => handleSourceTypeChange('Manual')}
                                            className="h-4 w-4 accent-blue-600"
                                        />
                                        <span className="text-sm dark:text-white">Manual</span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Widget Title */}
                        {contentType === 'Static' && sourceType === "Manual" && (
                            <>
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Widget Title  <span className="text-red-700">*</span>
                                        <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                            <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                                Static text label for the widget header or auto-source from single entity (category/collection name). Use (category_name)/(collection_name) for dynamic titles on contextual pages. Max 50 characters recommended.
                                            </div>
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        value={widgetTitle}
                                        // onChange={(e) => setWidgetTitle(e.target.value)}
                                        onChange={(e) => handleWidgetTitleChange(e.target.value)}
                                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm
                            focus:outline-none focus:ring-2 focus:ring-blue-500
                            dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                                    />
                                    {errors.widgetTitle && (
                                        <p className="mt-1 text-sm text-red-500">{errors.widgetTitle}</p>
                                    )}
                                </div>

                                {/* Widget Icon */}
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                                        Widget Icon <span className="text-red-700">*</span>
                                    </label>

                                    <div className="max-w-xs">
                                        {widgetIcon ? (
                                            <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={
                                                            widgetIcon.icon.startsWith('http')
                                                                ? widgetIcon.icon
                                                                : getImageSrc(widgetIcon.icon)
                                                        }
                                                        alt="Selected icon"
                                                        className="w-6 h-6 object-contain"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">
                                                        {widgetIcon.name}
                                                    </span>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setWidgetIcon(null);
                                                        clearFieldError('widgetIcon');
                                                    }}
                                                    className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <IconDropdown
                                                value={widgetIcon}
                                                onChange={(selectedIcon: any) => {
                                                    if (!selectedIcon) return;

                                                    const iconPath =
                                                        selectedIcon.icon ||
                                                        selectedIcon.path ||
                                                        selectedIcon.image ||
                                                        '';

                                                    if (!iconPath) return;

                                                    setWidgetIcon({
                                                        name: selectedIcon.name,
                                                        icon: iconPath,
                                                    });

                                                    clearFieldError('widgetIcon');
                                                }}
                                            />
                                        )}

                                        {errors.widgetIcon && (
                                            <p className="mt-1 text-sm text-red-500">
                                                {errors.widgetIcon}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {contentType === 'Static' && sourceType === 'Category' && (
                            <div className="mb-4">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                                    Category
                                </label>
                                <SearchableCategoryDropdown
                                    value={selectedCategory}
                                    onSelect={setSelectedCategory}
                                    placeholder="Search category..."
                                />
                                {selectedCategory && (categoryContentQuery.isLoading || isFilteringShows) && (
                                    <p className="text-xs text-slate-500 mt-1">Loading shows...</p>
                                )}
                                {selectedCategory && !categoryContentQuery.isLoading && !isFilteringShows && availableShows.length === 0 && (
                                    <p className="text-xs text-slate-500 mt-1">No shows with ≥ 2 episodes found</p>
                                )}
                                {selectedCategory && !isFilteringShows && availableShows.length > 0 && (
                                    <p className="text-xs text-green-600 mt-1">
                                        Found {availableShows.length} show(s) with ≥ 2 episodes
                                    </p>
                                )}
                            </div>
                        )}

                        {contentType === 'Static' && sourceType === 'Collection' && (
                            <div className="mb-4">
                                <label className="text-sm font-medium text-slate-700 mb-1 block dark:text-slate-50">
                                    Collection
                                </label>
                                <SearchableCollectionDropdown
                                    value={selectedCollection}
                                    onSelect={setSelectedCollection}
                                    placeholder="Search collection..."
                                />
                                {selectedCollection && (collectionContentQuery.isLoading || isFilteringShows) && (
                                    <p className="text-xs text-slate-500 mt-1">Loading shows...</p>
                                )}
                                {selectedCollection && !collectionContentQuery.isLoading && !isFilteringShows && availableShows.length === 0 && (
                                    <p className="text-xs text-slate-500 mt-1">No shows with ≥ 2 episodes found</p>
                                )}
                                {selectedCollection && !isFilteringShows && availableShows.length > 0 && (
                                    <p className="text-xs text-green-600 mt-1">
                                        Found {availableShows.length} show(s) with ≥ 2 episodes
                                    </p>
                                )}
                            </div>
                        )}
                        {/* {sourceType === 'Manual' && (
                            <div className="mb-4">

                                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mt-4">
                                    <span className="font-medium">
                                        Item Count
                                    </span>
                                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                        {showItems.length}/{MAX_ITEMS}
                                    </span>
                                </div>
                            </div>
                        )} */}

                        {/* Sorting */}
                        {contentType === 'Static' && (sourceType === 'Category' || sourceType === 'Collection') && (
                            <div className="mt-4">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                                    Sorting Method
                                </label>
                                <SingleSelect
                                    value={sortingMethod}
                                    options={sortingOptions}
                                    placeholder="Select Option"
                                    onChange={(option) => setSortingMethod(option.value)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT SIDE – ITEMS & CARD DETAILS */}
                {contentType === 'Static' && sourceType === 'Manual' && (
                    <div className="space-y-4 lg:col-span-3">
                        {/* Show Search */}
                        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                                Select Shows  <span className="text-red-700">*</span>
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                        Search bar to find and add shows for manual selection. Only shows with ≥2 published episodes qualify for Show Card widgets. Multi-select allowed up to max items limit.
                                    </div>
                                </span>
                            </label>


                            <div ref={showSearchRef} className="relative">
                                <input
                                    type="text"
                                    value={showSearch}
                                    placeholder="Search shows by name..."
                                    onChange={(e) => {
                                        setShowSearch(e.target.value)
                                        setShowSearchOpen(true)
                                    }}
                                    onFocus={() => setShowSearchOpen(true)}
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm
                                text-slate-900 placeholder-slate-400
                                focus:outline-none focus:ring-2 focus:ring-blue-500
                                dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                                />
                                {errors.showItems && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {errors.showItems}
                                    </p>
                                )}
                                {showSearchOpen && (showSearchQuery.isLoading || showSearchQuery.isFetching || showSearchResults.length > 0) && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg dark:bg-slate-800 dark:border-slate-600">
                                        {(showSearchQuery.isLoading || showSearchQuery.isFetching) && (
                                            <div className="px-3 py-2 text-sm text-slate-500">
                                                Searching...
                                            </div>
                                        )}

                                        {!showSearchQuery.isLoading && !showSearchQuery.isFetching && showSearchResults.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-slate-500">
                                                No shows found with ≥ 2 episodes
                                            </div>
                                        )}

                                        {showSearchResults.map((show) => {
                                            const isAdded = showItems.some(item => item.id === show.id)
                                            return (
                                                <div
                                                    key={show.id}
                                                    onClick={() => !isAdded && handleShowSelect(show)}
                                                    className={`cursor-pointer px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-3 ${isAdded ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                >
                                                    {show.thumbnail && (
                                                        <img
                                                            src={getImageSrc(show.thumbnail)}
                                                            alt={show.label}
                                                            className="w-10 h-6 object-cover rounded"
                                                        />
                                                    )}
                                                    <span className="flex-1">{show.label}</span>
                                                    {isAdded && (
                                                        <span className="text-xs text-green-600">Added</span>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <div className='flex items-center justify-between'>
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                                    Selected Shows
                                </h3>
                                {/* Tooltip */}
                                <div className="relative flex-item group ml-2 mt-0">
                                    {/* Item Count */}
                                    <div className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 ">

                                        <span
                                            className={`
                          rounded-full px-2 py-0.5 text-xs font-medium
                          ${showItems.length >= MAX_ITEMS
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-blue-100 text-blue-700'}
                      `}
                                        > ({showItems.length}/{MAX_ITEMS})</span>
                                    </div>

                                    <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
                                                                    ">
                                        You can select up to {MAX_ITEMS} items <span className='font-medium'>({showItems?.length} selected)</span>.
                                    </div>
                                </div>
                            </div>

                            {showItems.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                    No shows selected. Search and select shows above.
                                </p>
                            ) : (
                                <div className="space-y-5">
                                    {showItems.map((item, index) => (
                                        <CarouselItem
                                            key={item.id}
                                            item={item}
                                            index={index}
                                            total={showItems.length}
                                            onMoveUp={() => moveItem(index, index - 1)}
                                            onMoveDown={() => moveItem(index, index + 1)}
                                            onDelete={() => deleteItem(index)}
                                        >
                                            <div className="flex items-center gap-3">
                                                {item.thumbnail && (
                                                    <img
                                                        src={getImageSrc(item.thumbnail)}
                                                        alt={item.title || 'Thumbnail'}
                                                        className="w-15 h-12 object-cover rounded border"
                                                    />
                                                )}
                                                <div>
                                                    {item.title && (
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                            {item.title}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-gray-500">
                                                        Show ID: {item.redirection_id}
                                                    </p>
                                                </div>
                                            </div>
                                        </CarouselItem>
                                    ))}
                                </div>
                            )}
                        </div>
                        {showItems.length > 0 && (
                            <CardDetails
                                showIds={manualShowIds}
                                cardDetails={cardDetails}
                                onCardDetailsChange={setCardDetails}
                                onSave={handleSave}
                            />
                        )}
                    </div>
                )}

                {contentType === 'Static' && sourceType === 'Category' && filteredShows.length > 0 && (
                    <SelectableShowCards
                        filteredShows={filteredShows}
                        isFilteringShows={isFilteringShows}
                        sourceType="Category"
                        currentState={categoryState}
                        setState={setCategoryState}
                        onSave={handleSave}
                    />
                )}
                {contentType === 'Static' && sourceType === 'Collection' && filteredShows.length > 0 && (
                    <SelectableShowCards
                        filteredShows={filteredShows}
                        isFilteringShows={isFilteringShows}
                        sourceType="Collection"
                        currentState={collectionState}
                        setState={setCollectionState}
                        onSave={handleSave}
                    />
                )}
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 pt-5 dark:border-slate-700">
                <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-md bg-slate-200 px-5 py-2 text-sm font-medium
                text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-white"
                >
                    Cancel
                </button>

                <button
                    onClick={handleSave}
                    className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium
                text-white hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </div>
    );
}

export default ShowCard
